import type { Company, CompanyStatus } from './types.ts';
import { COMPANY_STATUSES } from './companyStatus.ts';

/** Value of the status filter — a concrete status, or `'all'` for no filter. */
export type StatusFilter = CompanyStatus | 'all';

/**
 * Sentinel filter value matching companies whose field is empty/null. A
 * double-underscore token can't collide with real human-entered free text.
 */
export const FIELD_NOT_SET = '__none__';

/** The free-text company-info fields the list can filter on. */
export type FilterableField = 'contractor_type' | 'team_region';

export type CompanyFilters = {
  /** Free-text search matched against company/contact/email, company number,
   *  contract number and the company-filled primary contact name/email; '' = no search. */
  query: string;
  /** 'all' = every active status (deactivated hidden); 'any' = no status
   *  condition at all — used by the faceted pill counts, which must see
   *  deactivated rows to count them under their own pill. */
  status: StatusFilter | 'any';
  /** Selected contractor types — OR within the field. `[]` = no filter; a
   *  FIELD_NOT_SET entry matches companies whose field is empty/whitespace;
   *  other entries are exact (trimmed) matches. */
  contractorType: string[];
  /** Same contract as contractorType. */
  teamRegion: string[];
  /** ABN verification filter: 'verified' = only ABN-verified companies,
   *  'unverified' = only non-verified, null = no filter. */
  abnVerified: 'verified' | 'unverified' | null;
};

// [] → no filter (field condition disabled). Otherwise the company matches when
// its (trimmed) field value is in the selected set — OR within the field — where
// a FIELD_NOT_SET entry matches an empty/whitespace value. Values are stored
// trimmed by the create modal, but trim here too in case other write paths ever
// differ.
function matchesField(value: string | null, filters: string[]): boolean {
  if (filters.length === 0) return true;
  const v = (value ?? '').trim();
  if (v === '') return filters.includes(FIELD_NOT_SET);
  return filters.includes(v);
}

/**
 * Filter the admin company list. All conditions AND together: a free-text query
 * (matched against company name, contact name and email, company number, contract
 * number and the company-filled primary contact name/email), a status, and
 * set-membership matches on the contractor type / team region fields
 * (OR within each field). An empty array disables that field's condition.
 * For status: `'all'` keeps every *active* status (deactivated rows hidden),
 * `'any'` applies no status condition at all (deactivated rows included — used by
 * the faceted pill counts), and a concrete status keeps only that status.
 */
export function filterCompanies(companies: Company[], filters: CompanyFilters): Company[] {
  const q = filters.query.trim().toLowerCase();
  return companies.filter((c) => {
    if (filters.status === 'any') {
      // no status condition — faceted counts need deactivated rows included
    } else if (filters.status === 'all') {
      // 'all' means every *active* status — deactivated companies appear only
      // when their pill is selected explicitly.
      if (c.status === 'deactivated') return false;
    } else if (c.status !== filters.status) {
      return false;
    }
    if (!matchesField(c.contractor_type, filters.contractorType)) return false;
    if (!matchesField(c.team_region, filters.teamRegion)) return false;
    if (filters.abnVerified === 'verified' && !c.abn_verified) return false;
    if (filters.abnVerified === 'unverified' && c.abn_verified) return false;
    if (!q) return true;
    // Nullable company-filled / admin fields default to '' so a missing value
    // never matches (and never throws).
    return (
      c.company_name.toLowerCase().includes(q) ||
      c.contact_name.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      (c.company_number ?? '').toLowerCase().includes(q) ||
      (c.contract_number ?? '').toLowerCase().includes(q) ||
      (c.primary_contact_name ?? '').toLowerCase().includes(q) ||
      (c.contact_email ?? '').toLowerCase().includes(q)
    );
  });
}

export type FieldValues = {
  /** Distinct non-empty trimmed values, deduped and localeCompare-sorted. */
  values: string[];
  /** True when at least one company has the field empty — drives "(Not set)". */
  hasNotSet: boolean;
};

/**
 * Distinct values of a free-text company-info field across the *unfiltered*
 * list, for the filter dropdowns (options stay stable while other filters
 * narrow the view). Dedupe is exact/case-sensitive: contractor types come from
 * a curated dropdown, and merging "North"/"north" heuristically would be
 * unpredictable.
 */
export function distinctFieldValues(companies: Company[], field: FilterableField): FieldValues {
  const set = new Set<string>();
  let hasNotSet = false;
  for (const c of companies) {
    const v = (c[field] ?? '').trim();
    if (v === '') hasNotSet = true;
    else set.add(v);
  }
  return { values: [...set].sort((a, b) => a.localeCompare(b)), hasNotSet };
}

/**
 * FieldValues for a field whose dropdown options are curated in Settings
 * (branding) rather than derived from the data. Options keep their configured
 * order (matching the Settings editor and the create-company modal), trimmed,
 * with empties and duplicates dropped. (Settings already persists the list
 * through `normalizeStringList`, which trims/dedupes — the re-normalisation
 * here is belt-and-braces for any other write path.) `hasNotSet` reports whether
 * any company currently misses the field (informational; the "(Not set)" filter
 * is offered for these fields regardless, see parseFieldFilterParam).
 */
export function configuredFieldValues(
  configured: string[],
  companies: Company[],
  field: FilterableField,
): FieldValues {
  const seen = new Set<string>();
  const values: string[] = [];
  for (const raw of configured) {
    const v = raw.trim();
    if (v !== '' && !seen.has(v)) {
      seen.add(v);
      values.push(v);
    }
  }
  return { values, hasNotSet: companies.some((c) => (c[field] ?? '').trim() === '') };
}

export type StatusCounts = {
  /** Count per status — every status in COMPANY_STATUSES is present (0 when none). */
  counts: Record<CompanyStatus, number>;
  /** Number of *active* companies — deactivated rows count only under their own pill. */
  total: number;
};

// Maps a raw ?status= query value to a StatusFilter, defaulting unknown/empty
// values to 'all' so a hand-edited URL can never crash the filter.
export function parseStatusFilterParam(raw: string | null): StatusFilter {
  if (raw && (COMPANY_STATUSES as readonly string[]).includes(raw)) {
    return raw as StatusFilter;
  }
  return 'all';
}

/**
 * Maps the repeatable raw ?type= / ?region= query values to a
 * validated, deduped field-filter array. FIELD_NOT_SET always passes through —
 * it's an admin-meaningful "find companies missing this field" filter, offered
 * regardless of whether any company currently has it empty (it simply returns no
 * rows when none do). A concrete value passes only while it exists in the data;
 * anything else (hand-edited URL, stale link after the last matching company was
 * deleted) is dropped so the control can never silently disagree with the table.
 * Order follows `allowed.values` (with FIELD_NOT_SET last) for a deterministic URL.
 */
export function parseFieldFilterParam(raw: string[], allowed: FieldValues): string[] {
  const wanted = new Set(raw);
  const out = allowed.values.filter((v) => wanted.has(v));
  if (wanted.has(FIELD_NOT_SET)) out.push(FIELD_NOT_SET);
  return out;
}

/**
 * Tally companies by status for the filter strip's count badges. Every status is
 * seeded to 0 first, so the strip renders a stable, complete set of pills without
 * each one having to guard for a missing key.
 */
export function countByStatus(companies: Company[]): StatusCounts {
  const counts = Object.fromEntries(COMPANY_STATUSES.map((s) => [s, 0])) as Record<
    CompanyStatus,
    number
  >;
  let total = 0;
  for (const c of companies) {
    if (c.status in counts) counts[c.status] += 1;
    if (c.status !== 'deactivated') total += 1;
  }
  return { counts, total };
}
